import type { CityConfig } from "../city-template/types";

export const rialtoConfig: CityConfig = {
  city: "Rialto",
  state: "California",
  stateAbbr: "CA",
  slug: "rialto",
  route: "/cities/rialto",
  title: "Emergency Passport Rialto, CA | Fast Passport Renewal & Expedited Help",
  metaDescription: "Emergency passport and expedited passport services in Rialto, CA — as fast as 24 hours. Fast passport renewal, same-day help, and child passport expediting for San Bernardino County and Inland Empire travelers. A+ BBB rated, registered State Dept. courier.",
  h1: "Emergency & Expedited Passport Services in Rialto, CA",
  heroSubtitle: "Passport expired before your trip? Need an emergency passport fast? We help Rialto and Inland Empire travelers get their expedited passports as quickly as 24 hours. A+ BBB rated. No office visit required.",
  heroImageUrl: "https://readdy.ai/api/search-image?query=Rialto%20California%20suburban%20cityscape%20at%20golden%20hour%20with%20the%20San%20Bernardino%20Mountains%20in%20the%20background%2C%20modern%20residential%20neighborhoods%20and%20commercial%20districts%20along%20historic%20Route%2066%2C%20warm%20sunset%20light%20over%20the%20Inland%20Empire%2C%20professional%20landscape%20photography%2C%20wide%20panoramic%20view%2C%20no%20visible%20text%20or%20people%2C%20suburban%20community%20with%20majestic%20mountain%20backdrop%20and%20warm%20amber%20tones&width=1800&height=700&seq=rialto-hero-001&orientation=landscape",
  heroImageAlt: "Rialto California cityscape at golden hour with San Bernardino Mountains in the background",
  geoPosition: "34.1064;-117.3703",
  geoPlacename: "Rialto, California",
  regionalAgency: {
    name: "Los Angeles Passport Agency",
    address: "300 N Los Angeles St, Los Angeles, CA 90012",
    distance: "Located in downtown LA — approximately 45–60 minutes from Rialto via I-10",
  },
  nearbyCitiesZips: [
    { name: "Rialto", zips: "92376, 92377" },
    { name: "San Bernardino", zips: "92401–92408, 92410–92415, 92418, 92420, 92423, 92424, 92427" },
    { name: "Fontana", zips: "92331, 92334, 92335, 92336, 92337" },
    { name: "Colton", zips: "92313, 92324" },
    { name: "Rancho Cucamonga", zips: "91701, 91729, 91730, 91737, 91739" },
    { name: "Highland", zips: "92346, 92386" },
    { name: "Redlands", zips: "92373, 92374, 92375, 92399" },
    { name: "Loma Linda", zips: "92350, 92354, 92357" },
    { name: "Ontario", zips: "91758, 91761, 91762, 91764" },
    { name: "Bloomington", zips: "92316" },
    { name: "Grand Terrace", zips: "92313, 92324" },
    { name: "Moreno Valley", zips: "92551, 92552, 92553, 92555, 92557" },
  ],
  benefits: [
    { icon: "ri-time-line", title: "24–48 Hour Processing", desc: "Emergency courier submission for urgent Inland Empire departures" },
    { icon: "ri-shield-check-line", title: "Registered Courier", desc: "U.S. Department of State approved passport expediting" },
    { icon: "ri-user-line", title: "Dedicated Agent", desc: "A real person assigned to your case from start to finish" },
    { icon: "ri-mail-send-line", title: "Document Shipping", desc: "Prepaid overnight labels to our processing center" },
  ],
  internalLinks: [
    { name: "Los Angeles", route: "/cities/los-angeles" },
    { name: "San Diego", route: "/cities/san-diego" },
    { name: "Riverside", route: "/cities/riverside" },
    { name: "San Bernardino", route: "/cities/san-bernardino" },
    { name: "Ontario", route: "/cities/ontario" },
    { name: "Rancho Cucamonga", route: "/cities/rancho-cucamonga" },
    { name: "Moreno Valley", route: "/cities/moreno-valley" },
  ],
  fpcOfficeNote: "Fast Passport Center serves Rialto remotely via secure courier. We do not have a walk-in office in Rialto — our registered couriers hand-deliver your documents directly to U.S. Department of State processing facilities. This is how we achieve 24–48 hour turnaround for Inland Empire travelers.",
  fpcOfficeDistance: "Serving all of San Bernardino County",
  serviceAreaIntro: "From the historic downtown along Route 66 to the newer developments in the north, Fast Passport Center serves the entire Rialto community. We regularly help travelers from San Bernardino, Fontana, Colton, and Highland get their passports fast.",
  nearbyCitiesIntro: "We serve all Rialto neighborhoods and nearby Inland Empire cities including San Bernardino, Fontana, Colton, Rancho Cucamonga, Highland, and Redlands. If you are in San Bernardino County, we have you covered.",
  howItWorksIntro: "Most Rialto travelers complete everything online and ship documents overnight. No need to drive to Los Angeles or San Diego or wait in line at the regional agency.",
  mistakesIntro: "With Ontario International Airport and LAX serving as the closest international airports, Rialto travelers can't afford passport delays. Here are the most common mistakes we see.",
  testimonialsIntro: "San Bernardino County and Inland Empire travelers trust us when their departure is days away. Here is what some of them have to say.",
  faqIntro: "Have questions about expedited passports in Rialto? Here are the most common ones we hear from San Bernardino County travelers.",
  statePageRoute: "/states/california",
  statePageName: "California",
  stickyNavLinks: [
    { label: "Processing", href: "#processing-options" },
    { label: "Services", href: "#services" },
    { label: "Service Area", href: "#service-area" },
    { label: "Facilities", href: "#acceptance-facilities" },
    { label: "Comparison", href: "#comparison" },
    { label: "How It Works", href: "#how-it-works" },
    { label: "FAQ", href: "#faq" },
    { label: "Resources", href: "#local-resources" },
  ],
  processingOptions: [
    {
      speed: "24",
      unit: "Hours*",
      label: "Regional Agency Processing",
      tag: "Emergency Only",
      tagColor: "bg-gray-100 text-gray-500",
      features: ["Appointment required — no walk-ins allowed", "Life-or-death emergencies only", "Bereavement or medical crisis", "Expect to wait all day or return next day"],
      highlight: false,
      emergency: true,
    },
    {
      speed: "3–9",
      unit: "Business Days",
      label: "Fast Passport Center",
      tag: "Most Popular",
      tagColor: "bg-[#F6C344] text-[#1A365D]",
      features: ["Registered U.S. Department of State Courier", "Reserved agency submission", "Predictable turnaround", "Ideal for upcoming trips", "Document review included", "Dedicated agent assigned to your case", "Daily status updates — never left guessing", "Not affected by government shutdowns"],
      highlight: true,
    },
    {
      speed: "3–5",
      unit: "Weeks",
      label: "Fast Passport Center",
      tag: "Standard Expedited",
      tagColor: "bg-[#1A365D]/10 text-[#1A365D]",
      features: ["Registered U.S. Department of State Courier", "Reserved agency submission", "Faster than self-service", "Good for planned travel", "Document review included", "Dedicated agent assigned to your case", "Daily status updates — never left guessing", "Not affected by government shutdowns"],
      highlight: false,
      smartSaver: true,
    },
    {
      speed: "1–3",
      unit: "Months",
      label: "Self-Service (Post Office)",
      tag: "Slowest",
      tagColor: "bg-gray-100 text-gray-400",
      features: ["No courier access — standard mail only", "Workload & wait times shift with shutdowns and budget fights", "In-person clerk appointment required", "No document review — errors cause rejection", "No dedicated agent — you are on your own", "Updates only by physical mail letter", "No phone or email contact for your case", "Can't upgrade speed after submission", "Not suitable if travel is within 10 weeks"],
      highlight: false,
      muted: true,
    },
  ],
  services: [
    {
      icon: "ri-flashlight-line",
      title: "Emergency Passport",
      description: "For travelers with immediate departure needs — as fast as 24 hours for urgent Inland Empire international flights.",
      bullets: ["24–48 hour turnaround available", "Registered courier hand-delivery", "Flight verification support"],
      badge: "Most Popular",
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-passport-line",
      title: "Passport Renewal",
      description: "Fast renewal for adults with a passport issued within the last 15 years at age 16 or older.",
      bullets: ["No clerk visit required for most", "Mail-in or courier options", "Lost passport replacement available"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=passport-renewal",
    },
    {
      icon: "ri-add-circle-line",
      title: "New Passport",
      description: "First-time passport application guidance with acceptance clerk appointment support.",
      bullets: ["Acceptance facility locator", "Form DS-11 assistance", "Photo requirement guidance"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-alert-line",
      title: "Lost or Stolen Passport",
      description: "Passport replacement with DS-64 form guidance and police report documentation.",
      bullets: ["DS-64 form assistance", "Identity verification support", "Emergency replacement options"],
      badge: "Urgent",
      link: "https://fastpassportcenter.com/checkout/?passport=lost-stolen-passport",
    },
    {
      icon: "ri-parent-line",
      title: "Child Passport",
      description: "Passport services for minors under 16 with parental consent and acceptance clerk support.",
      bullets: ["DS-11 form for minors", "Parental consent guidance", "Both parents presence rules"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=child-passport",
    },
    {
      icon: "ri-file-edit-line",
      title: "Name Change",
      description: "Passport correction and name change service after marriage, divorce, or legal changes.",
      bullets: ["DS-5504 or DS-82 guidance", "Marriage certificate support", "Court order documentation"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=name-change",
    },
  ],
  servicesSchema: [
    { name: "Emergency Passport", description: "Urgent passport processing for travelers with immediate departure needs, as fast as 24 hours." },
    { name: "Passport Renewal", description: "Fast passport renewal for U.S. citizens with a passport issued within the last 15 years at age 16 or older." },
    { name: "New Passport", description: "First-time passport application assistance with step-by-step guidance through the acceptance clerk process." },
    { name: "Lost or Stolen Passport", description: "Passport replacement service for lost or stolen passports, including DS-64 form guidance." },
    { name: "Child Passport", description: "Passport services for minors under 16, with parental consent guidance and acceptance clerk support." },
    { name: "Name Change", description: "Passport correction and name change service after marriage, divorce, or legal name changes." },
  ],
  testimonials: [
    {
      name: "Maria G.",
      location: "Rialto, CA",
      rating: 5,
      text: "My family trip to Puerto Vallarta was in 4 days and my passport was expired. Fast Passport Center got me a new one in 48 hours. Saved our entire vacation.",
      service: "Emergency Passport",
    },
    {
      name: "Michael B.",
      location: "San Bernardino, CA",
      rating: 5,
      text: "Needed to renew for a business trip to Tokyo. The process was smooth, they reviewed my documents, and I had my passport in 3 days. Highly recommend.",
      service: "Passport Renewal",
    },
    {
      name: "Sandra W.",
      location: "Fontana, CA",
      rating: 5,
      text: "Lost my passport the week before my cruise to the Bahamas. They walked me through the DS-64 process and got me a replacement in 2 days. Incredible service.",
      service: "Lost Passport",
    },
    {
      name: "Kevin J.",
      location: "Colton, CA",
      rating: 5,
      text: "As a frequent international traveler from the Inland Empire, I can't afford passport delays. Fast Passport Center has been my go-to for years. Professional and reliable.",
      service: "Expedited Renewal",
    },
  ],
  facilities: [
    {
      id: "rialto-1",
      name: "Rialto Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "550 N Riverside Ave",
      city: "Rialto",
      state: "CA",
      zip: "92376",
      phone: "(909) 875-2384",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "0.5 miles from downtown Rialto",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Acceptance hours are limited. Appointments required. Call ahead to confirm passport acceptance times.",
      icon: "ri-mail-line",
    },
    {
      id: "rialto-2",
      name: "San Bernardino Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "390 W 5th St",
      city: "San Bernardino",
      state: "CA",
      zip: "92401",
      phone: "(909) 384-1564",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "4.5 miles from Rialto",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Busy location — book appointments 2+ weeks in advance. Limited weekend hours.",
      icon: "ri-mail-line",
    },
    {
      id: "rialto-3",
      name: "Fontana Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "16615 Arrow Blvd",
      city: "Fontana",
      state: "CA",
      zip: "92335",
      phone: "(909) 355-3520",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "3.2 miles from Rialto",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Popular for northern San Bernardino County residents. Appointment strongly recommended.",
      icon: "ri-mail-line",
    },
    {
      id: "rialto-4",
      name: "Colton Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "1070 N La Cadena Dr",
      city: "Colton",
      state: "CA",
      zip: "92324",
      phone: "(909) 825-2261",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "2.8 miles from Rialto",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Major facility for central San Bernardino County. Photo services available. Book ahead.",
      icon: "ri-mail-line",
    },
    {
      id: "rialto-5",
      name: "Rancho Cucamonga Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "11075 Foothill Blvd",
      city: "Rancho Cucamonga",
      state: "CA",
      zip: "91730",
      phone: "(909) 989-2520",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "18 miles from Rialto",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Convenient for western San Bernardino County residents. Limited passport hours — call to confirm.",
      icon: "ri-mail-line",
    },
    {
      id: "rialto-6",
      name: "San Bernardino County Clerk",
      type: "County Clerk Passport Acceptance",
      address: "222 W Hospitality Ln",
      city: "San Bernardino",
      state: "CA",
      zip: "92415",
      phone: "(909) 387-8306",
      hours: "Mon–Fri: 8:00 AM – 4:30 PM",
      distance: "4.0 miles from Rialto",
      website: "https://www.sbcounty.gov/",
      websiteLabel: "San Bernardino County",
      notes: "Major county facility with extended hours. Accepts walk-ins but appointments recommended for faster service.",
      icon: "ri-government-line",
    },
  ],
  comparisonRows: [
    { feature: "Appointment Needed", agency: "Yes — call NPIC, limited slots", fpc: "No — we handle everything for you" },
    { feature: "Processing Speed", agency: "Same-day if approved (rare)", fpc: "24–48 hours guaranteed for emergency" },
    { feature: "Document Review", agency: "No — rejected if wrong", fpc: "Yes — we check before submission" },
    { feature: "Travel to Agency", agency: "Yes — downtown Los Angeles", fpc: "No — ship from home or office" },
    { feature: "Agent Support", agency: "No — government clerk only", fpc: "Yes — dedicated agent assigned" },
    { feature: "Status Updates", agency: "No — no tracking provided", fpc: "Yes — daily email updates" },
    { feature: "Guaranteed Timing", agency: "No — subject to availability", fpc: "Yes — contractual turnaround times" },
  ],
  mistakes: [
    {
      icon: "ri-image-line",
      title: "Wrong Photo Size or Background",
      description: "Inland Empire photo shops often use generic sizes. Passport photos must be exactly 2×2 inches with a white or off-white background.",
    },
    {
      icon: "ri-file-list-line",
      title: "Incomplete DS-11 Form",
      description: "Missing signatures or using the wrong form type are the #1 reasons for rejection at Rialto acceptance facilities.",
    },
    {
      icon: "ri-money-dollar-circle-line",
      title: "Wrong Payment Amount",
      description: "Fees change frequently. Many Rialto applicants show up with incorrect checks and have to reschedule their appointment.",
    },
    {
      icon: "ri-calendar-close-line",
      title: "No Proof of Travel",
      description: "Without flight confirmation or travel itinerary, the LA regional agency will turn you away for emergency appointments.",
    },
    {
      icon: "ri-parent-line",
      title: "Missing Parental Consent",
      description: "For minors, both parents must appear or submit notarized consent. This trips up many San Bernardino County families at the clerk.",
    },
    {
      icon: "ri-time-line",
      title: "Waiting Too Long to Start",
      description: "Rialto travelers often assume standard processing will be fine. It takes 8–11 weeks. Start expedited at least 3 weeks before travel.",
    },
  ],
  steps: [
    { number: "1", icon: "ri-file-list-line", title: "Apply Online", description: "Complete your application on our secure website in under 10 minutes." },
    { number: "2", icon: "ri-mail-send-line", title: "Ship Documents", description: "We send you a prepaid overnight label. Drop your documents at any UPS location in Rialto." },
    { number: "3", icon: "ri-shield-check-line", title: "We Review", description: "Our team checks every document for accuracy before hand-delivering to the State Department." },
    { number: "4", icon: "ri-passport-line", title: "Processing", description: "Your passport is processed at a U.S. Department of State facility using our registered courier slots." },
    { number: "5", icon: "ri-truck-line", title: "Delivery", description: "We overnight your new passport back to your Rialto address — trackable every step of the way." },
  ],
  faqs: [
    { q: "Can I get an emergency passport in Rialto before international travel?", a: "Yes. Fast Passport Center provides emergency passport services in Rialto for travelers with urgent international flights. Our registered couriers achieve 24–48 hour turnaround for most Inland Empire travelers." },
    { q: "How fast can I renew my passport in Rialto?", a: "Fast Passport Center offers fast passport renewal in Rialto in as little as 24–48 hours through our emergency courier service." },
    { q: "What if my passport expires before my cruise?", a: "If your passport expires before your cruise, contact Fast Passport Center immediately. We can expedite your passport renewal in Rialto in 24–48 hours so you don't miss your departure." },
    { q: "Can I get a same-day passport appointment near Rialto?", a: "Same-day passport appointments are only available at the LA Regional Passport Agency for documented life-or-death emergencies. For all other urgent travel, Fast Passport Center's 24–48 hour emergency courier processing is the fastest guaranteed option for Rialto residents." },
    { q: "Can I expedite a child passport in Rialto?", a: "Yes. Fast Passport Center expedites child passports in Rialto for minors under 16. Both parents must appear or submit notarized consent, along with the child's birth certificate and both parents' IDs." },
    { q: "What should I do if I lost my passport before a trip?", a: "If you lost your passport before a trip, contact Fast Passport Center immediately. We provide lost passport replacement services in Rialto with DS-64 form guidance. Our emergency courier service can get your replacement passport in 24–48 hours." },
    {
      q: "How fast can I get a passport in Rialto?",
      a: "With Fast Passport Center's emergency courier service, Rialto travelers can receive their passport in as little as 24–48 hours. Our registered couriers hand-deliver your documents directly to U.S. Department of State processing facilities, bypassing standard mail delays.",
    },
    {
      q: "Do I need to drive to the Los Angeles Passport Agency?",
      a: "No. With Fast Passport Center, you complete everything online and ship your documents overnight. Our couriers handle the in-person delivery to State Department facilities. You only need to visit a local acceptance clerk if you're a first-time applicant or need a name change.",
    },
    {
      q: "Where are the closest passport acceptance facilities in Rialto?",
      a: "Popular options include the Rialto Post Office (92376), San Bernardino Post Office (92401), Fontana Post Office (92335), and Colton Post Office (92324). All require appointments. We help you find the most convenient location and schedule your visit.",
    },
    {
      q: "Can I get a same-day passport in Rialto?",
      a: "Same-day passports are only available at the LA Regional Passport Agency for documented life-or-death emergencies with proof of travel within 72 hours. For all other urgent travel, Fast Passport Center's 24–48 hour emergency processing is the fastest reliable option.",
    },
    {
      q: "What if I live in Bloomington or Grand Terrace?",
      a: "We serve all of San Bernardino County including Bloomington, Grand Terrace, Highland, and Loma Linda. Our overnight shipping labels work from any UPS location, so your location within the county doesn't affect processing speed.",
    },
    {
      q: "How much does expedited passport service cost in Rialto?",
      a: "Government fees are fixed: $130 for a passport book (adult) plus a $60 expedite fee. Fast Passport Center's service fees vary by processing speed — emergency 24–48 hour processing, 3–5 day expedited, or standard options. Contact us for a personalized quote based on your timeline.",
    },
    {
      q: "Do you help with passport photos in Rialto?",
      a: "We guide you on exact photo requirements and can recommend locations near you. Many CVS, Walgreens, and FedEx locations in Rialto, San Bernardino, and Fontana offer passport photo services. We review your photos before submission to ensure they meet State Department standards.",
    },
    {
      q: "What documents do I need for a first-time passport in Rialto?",
      a: "You'll need proof of citizenship (birth certificate or naturalization certificate), government-issued photo ID (driver's license or REAL ID), two passport photos, and payment. We provide a complete checklist and review your documents before you visit the acceptance clerk.",
    },
    {
      q: "Can you help with a child's passport in Rialto?",
      a: "Yes. Child passports require both parents (or notarized consent from the absent parent), the child's birth certificate, and both parents' IDs. We walk San Bernardino County families through the entire process and help schedule acceptance clerk appointments.",
    },
  ],
  photoLocations: [
    { name: "CVS Pharmacy — Rialto", address: "192 W Base Line Rd, Rialto, CA 92376", hours: "Mon–Sun: 8AM–10PM" },
    { name: "Walgreens — San Bernardino", address: "1925 E Highland Ave, San Bernardino, CA 92404", hours: "Mon–Sun: 7AM–11PM" },
    { name: "FedEx Office — Fontana", address: "15125 Summit Ave, Fontana, CA 92336", hours: "Mon–Fri: 7AM–9PM, Sat: 8AM–6PM" },
    { name: "UPS Store — Rancho Cucamonga", address: "10550 Foothill Blvd, Rancho Cucamonga, CA 91730", hours: "Mon–Fri: 8AM–7PM, Sat: 9AM–5PM" },
    { name: "Walmart — Fontana", address: "17251 Foothill Blvd, Fontana, CA 92335", hours: "Mon–Sun: 6AM–11PM" },
    { name: "Rite Aid — Redlands", address: "27520 W Lugonia Ave, Redlands, CA 92374", hours: "Mon–Sun: 8AM–10PM" },
    { name: "Postal Annex — Highland", address: "7225 Boulder Ave, Highland, CA 92346", hours: "Mon–Fri: 9AM–6PM, Sat: 9AM–4PM" },
    { name: "Costco — Rancho Cucamonga", address: "12220 Foothill Blvd, Rancho Cucamonga, CA 91739", hours: "Mon–Fri: 10AM–8:30PM, Sat–Sun: 9:30AM–6PM" },
  ],
  dmvLocations: [
    {
      name: "DMV — Rialto",
      address: "1425 S Riverside Ave, Rialto, CA 92376",
      hours: "Mon–Fri: 8AM–5PM",
      note: "REAL ID and driver's license renewals. Appointment required.",
      featured: true,
    },
    {
      name: "DMV — San Bernardino",
      address: "660 E Carnegie Dr, San Bernardino, CA 92408",
      hours: "Mon–Fri: 8AM–5PM",
      note: "Passport-ready photo services available. Online appointment booking.",
      featured: false,
    },
    {
      name: "DMV — Fontana",
      address: "9321 Sierra Ave, Fontana, CA 92335",
      hours: "Mon–Fri: 8AM–5PM",
      note: "REAL ID processing. Walk-ins limited — book online first.",
      featured: false,
    },
    {
      name: "DMV — Rancho Cucamonga",
      address: "8629 Hellman Ave, Rancho Cucamonga, CA 91730",
      hours: "Mon–Fri: 8AM–5PM",
      note: "Full-service center for western San Bernardino County residents.",
      featured: true,
    },
  ],
  airports: [
    {
      code: "ONT",
      name: "Ontario International Airport",
      distance: "15 miles from Rialto",
      address: "2900 E Airport Dr, Ontario, CA 91761",
      note: "The closest major airport for Rialto. Growing international flights to Mexico and domestic connections. Less crowded than LAX.",
      flightUrl: "https://www.flyontario.com/",
      flightLabel: "Ontario Airport",
      featured: true,
    },
    {
      code: "LAX",
      name: "Los Angeles International Airport",
      distance: "55 miles from Rialto via I-10",
      address: "1 World Way, Los Angeles, CA 90045",
      note: "Major international hub for long-haul flights to Asia, Europe, and South America. Accessible via I-10.",
      flightUrl: "https://www.flylax.com/",
      flightLabel: "LAX Flight Info",
      featured: false,
    },
    {
      code: "SNA",
      name: "John Wayne Airport (Orange County)",
      distance: "45 miles from Rialto via I-10 and I-57",
      address: "18601 Airport Way, Santa Ana, CA 92707",
      note: "International flights to Mexico, Canada, and connections to Europe. Alternative to LAX.",
      flightUrl: "https://www.ocair.com/",
      flightLabel: "John Wayne Airport",
      featured: false,
    },
  ],
};